import * as React from "react"
import { ImagePlus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { PhotoFileInput } from "@/components/ui/photo-file-input"
import { PhotoLibraryDialog } from "./PhotoLibraryDialog"
import type { LibraryPhoto } from "@/components/pages-cms/types"

export type PhotoSelection =
  | { kind: "unchanged" }
  | { kind: "library"; id: number; url: string | null }
  | { kind: "upload"; file: File }

// Form-integrated photo chooser: pick an existing library photo or upload a
// new one (which also lands in the library). The parent form owns the value
// and submits either photo_id (library pick) or photo (file upload).
export function PhotoSelectField({
  library,
  currentUrl,
  value,
  onChange,
  shape = "landscape",
  inputId = "photo",
}: {
  library: LibraryPhoto[]
  currentUrl?: string | null
  value: PhotoSelection
  onChange: (value: PhotoSelection) => void
  shape?: "landscape" | "avatar"
  inputId?: string
}) {
  const [pickerOpen, setPickerOpen] = React.useState(false)
  const [uploadPreview, setUploadPreview] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (value.kind !== "upload") {
      setUploadPreview(null)
      return
    }
    const url = URL.createObjectURL(value.file)
    setUploadPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [value])

  const previewUrl =
    value.kind === "library" ? value.url : value.kind === "upload" ? uploadPreview : (currentUrl ?? null)

  function selectFromLibrary(photo: LibraryPhoto) {
    onChange({ kind: "library", id: photo.id, url: photo.url })
  }

  return (
    <div className="space-y-3">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt="Selected photo"
          className={
            shape === "avatar"
              ? "h-20 w-20 rounded-full object-cover"
              : "aspect-video w-full max-w-xs rounded-lg border border-hairline object-cover"
          }
        />
      ) : (
        <div
          className={
            shape === "avatar"
              ? "flex h-20 w-20 items-center justify-center rounded-full bg-surface text-ink-muted"
              : "flex aspect-video w-full max-w-xs items-center justify-center rounded-lg border border-hairline bg-surface text-xs text-ink-muted"
          }
        >
          {shape === "avatar" ? <ImagePlus className="h-5 w-5" /> : "No photo yet"}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
          Choose from library
        </Button>
        <PhotoFileInput
          id={inputId}
          label="Upload photo"
          onSelect={(files) => onChange({ kind: "upload", file: files[0] })}
        />
      </div>
      {value.kind === "library" && (
        <p className="text-xs text-ink-muted">Photo selected from the library — save to apply.</p>
      )}
      {value.kind === "upload" && (
        <p className="text-xs text-ink-muted">
          New upload ({value.file.name}) — it will also be added to the photo library on save.
        </p>
      )}

      <PhotoLibraryDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        library={library}
        onSelect={selectFromLibrary}
      />
    </div>
  )
}
